import React, { useEffect, useRef } from "react";
import MuiDialog from "@mui/material/Dialog";
import { Button } from "./button";
import { IconX } from "@tabler/icons-react";
type DialogProps = {
    title?: string;
    titleIcon?: React.ReactNode;
    titleSize?: "sm" | "base" | "lg" | "xl" | "2xl";
    text?: string;
    content?: React.ReactNode;
    showButtons?: boolean;
    showCloseButton?: boolean;
    titleAlignment?: "left" | "center";
    open: boolean;
    onConfirm?: () => void;
    buttonText?: string;
    cancelText?: string;
    setOpen: (open: boolean) => void;
    width?: string;
    fullWidthMobile?: boolean;
    /**
     * The confirmed action is in flight. Every way out (cancel, close icon,
     * backdrop, Escape) is locked until it settles: the request can't be
     * called back, so offering "Cancel" then would be a lie.
     */
    busy?: boolean;
};
export function Dialog({ title, titleIcon, titleSize = "xl", text, content, showButtons = true, showCloseButton = false, titleAlignment = "center", open, setOpen, onConfirm, buttonText = "Continue", cancelText = "Cancel", width = "max-w-lg", fullWidthMobile = false, busy = false, }: DialogProps) {
    const titleSizeClasses = {
        sm: "text-sm",
        base: "text-base",
        lg: "text-lg",
        xl: "text-xl",
        "2xl": "text-2xl",
    };
    const contentRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (open) {
            contentRef.current?.focus();
        }
    }, [open]);
    const close = () => {
        if (!busy)
            setOpen(false);
    };
    return (<MuiDialog open={open} onClose={close} aria-labelledby="dialog-title" fullWidth fullScreen={fullWidthMobile} slotProps={{
            backdrop: {
                className: "bg-black bg-opacity-40",
            },
            paper: {
                className: `${width} max-h-[90vh] bg-bgDefault text-textPrimary shadow-xl rounded-lg`,
                sx: {
                    backgroundColor: "#FCFCFC", // bgDefault
                    backgroundImage: "none",
                    color: "#0E0E0E", // textPrimary
                },
            },
        }}>
      <div ref={contentRef} tabIndex={-1} className={`relative pt-5 pb-6 ${fullWidthMobile ? "" : "px-6 max-h-[90vh]"} overflow-y-auto`} style={{ outline: "none" }}>
        {showCloseButton && (<button onClick={close} disabled={busy} aria-label="Close" className="absolute right-4 top-4 text-textPrimary hover:text-textSecondary transition-colors z-10">
            <IconX className="w-6 h-6"/>
          </button>)}

        <div className="mt-3 sm:mt-0">
          <div id="dialog-title" className={`font-normal leading-6 text-textPrimary mb-4 flex items-center gap-4 ${titleSizeClasses[titleSize]} ${titleAlignment === "center" ? "justify-center" : "justify-start"}`}>
            {titleIcon}
            <h3>{title}</h3>
          </div>

          <div id="dialog-description">
            {text && (<div className="mt-2">
                <p className={`text-sm text-textSecondary ${titleAlignment === "center" ? "text-center" : "text-left"}`}>
                  {text}
                </p>
              </div>)}
            <div className="w-full">{content}</div>
          </div>
        </div>

        {showButtons && (<div className="mt-6 flex flex-col items-center space-y-3">
            {onConfirm && (<Button variant="primary" onClick={onConfirm} disabled={busy} className="min-w-[236px]">
                {buttonText}
              </Button>)}
            <Button variant="secondary" onClick={close} disabled={busy} className="min-w-[236px] m-0">
              {cancelText}
            </Button>
          </div>)}
      </div>
    </MuiDialog>);
}
